// ─── Market Segments — single source of truth ─────────────────────────
// Any change to labels, thresholds, tooltip copy or PSF band mapping
// happens HERE and propagates to the Market Intelligence tab.
//
// Product decision (Feb 2026):
//   • Segments are INVENTORY-based, not market-based. A single market
//     (e.g. Whitefield) can appear in multiple segments as long as the
//     corresponding PSF band exists in the dataset.
//   • The selected segment determines which PSF band the market card
//     shows (mid → mid, premium → premium, luxury → luxury). Affordable
//     Homes uses the mid band since the master dataset does not carry
//     a distinct "affordable" band.

export const SEGMENT_DEFS = [
    {
        key: "Affordable",
        label: "Affordable Homes",
        band: "mid",
        min: 0,
        max: 10000,
        tooltip: "Typical projects below ₹10,000/sq.ft.",
    },
    {
        key: "Mid",
        label: "Mid Segment",
        band: "mid",
        min: 10000,
        max: 15000,
        tooltip: "Typical projects between ₹10,000–₹15,000/sq.ft.",
    },
    {
        key: "Premium",
        label: "Premium Segment",
        band: "premium",
        min: 15000,
        max: 20000,
        tooltip: "Typical projects between ₹15,000–₹20,000/sq.ft.",
    },
    {
        key: "Luxury",
        label: "Luxury Segment",
        band: "luxury",
        min: 20000,
        max: Infinity,
        tooltip: "Typical projects above ₹20,000/sq.ft.",
    },
];

export const SEGMENT_LABEL = SEGMENT_DEFS.reduce((acc, s) => {
    acc[s.key] = s.label;
    return acc;
}, {});

// PSF band a segment displays.
export function bandKeyForSegment(segKey) {
    const def = SEGMENT_DEFS.find((s) => s.key === segKey);
    return def ? def.band : "mid";
}

// PSF value that a market shows when viewed under a given segment.
// Returns null if the market has no data for that band.
export function psfForSegment(market, segKey) {
    const band = bandKeyForSegment(segKey);
    const bands = market.psfBands || {};
    return bands[band] ?? null;
}

// Whether a market should appear under a segment. Inventory rule:
//   • Affordable — mid PSF < ₹10,000
//   • Mid        — mid PSF in [₹10,000, ₹15,000]
//   • Premium    — has premium band data AND premium PSF ≥ ₹15,000
//   • Luxury     — has luxury band data AND luxury PSF ≥ ₹20,000
// This lets one market (e.g. Whitefield mid ₹13k / premium ₹20k) appear
// in both Mid and Premium.
export function marketMatchesSegment(market, segKey) {
    const bands = market.psfBands || {};
    const mid = bands.mid ?? market.psf ?? 0;
    if (segKey === "Affordable") return mid > 0 && mid < 10000;
    if (segKey === "Mid") return mid >= 10000 && mid <= 15000;
    if (segKey === "Premium") return bands.premium != null && bands.premium >= 15000;
    if (segKey === "Luxury") return bands.luxury != null && bands.luxury >= 20000;
    return false;
}
